// Shared vocabulary for the reharm moves: proposals, spelling, splitting a
// chord's beats, the dominant tests. Kept apart from the move files so rungs
// 0–2 and 3–5 can both import it without a cycle.

import type { ChordTypeId } from '../chords';
import { qualityFamily } from '../corpus';
import { functionalReading, type FunctionalReading, type KeyRegion } from '../analysis';
import { conventionalTonicName, pitchClassOf, spellNote, spellNoteInKey } from '../notes';
import type { SequenceChord } from '../song';
import type { ReharmCandidate, ReharmChordSpec, ReharmContext, ReharmMove } from './types';

export type Proposal = Omit<ReharmCandidate, 'id' | 'score'>;
export type Family = ReturnType<typeof qualityFamily>;

export const mod12 = (n: number) => ((n % 12) + 12) % 12;

export const MAJOR_SCALE = new Set([0, 2, 4, 5, 7, 9, 11]);
export const MINOR_SCALE = new Set([0, 2, 3, 5, 7, 8, 10, 11]); // natural + leading tone

export function regionAt(ctx: ReharmContext, at: number): KeyRegion | undefined {
  return ctx.analysis.regions.find((r) => at >= r.from && at <= r.to);
}

/** Spell a root for the region: diatonic degrees in the key's own accidentals,
 *  chromatic degrees FLAT (♭II, ♭VII, ♭VI, ♭III — the jazz convention for
 *  borrowed roots, which is what these moves write). */
export function spellRoot(pc: number, region: KeyRegion | undefined): string {
  if (!region) return spellNote(pc, 'flat');
  const degree = mod12(pc - region.tonicPc);
  const scale = region.minor ? MINOR_SCALE : MAJOR_SCALE;
  if (scale.has(degree)) {
    const tonic = conventionalTonicName(region.tonicPc, region.minor ? 'minor' : 'major');
    return spellNoteInKey(pc, { tonic, mode: region.minor ? 'minor' : 'major' });
  }
  return spellNote(pc, 'flat');
}

export const symbolOf = (spec: ReharmChordSpec): string => {
  const suffix: Partial<Record<ChordTypeId, string>> = {
    maj7: '∆',
    maj6: '6',
    maj9: '∆9',
    maj69: '6/9',
    maj7s5: '∆♯5',
    maj7b5: '∆♭5',
    dom7: '7',
    dom9: '9',
    dom13: '13',
    dom7b9: '7♭9',
    dom7s9: '7♯9',
    dom7sus4: '7sus4',
    dom7s5: '7♯5',
    dom7b5: '7♭5',
    min7: 'm7',
    min9: 'm9',
    min6: 'm6',
    min69: 'm6/9',
    minmaj7: 'm∆7',
    min7s5: 'm7♯5',
    min7b5: 'ø7',
    min7b59: 'ø9',
    min7b5b9: 'ø7♭9',
    dim7: '°7',
    dimmaj7: '°∆7',
    // The triad tier prints bare, the way a lead sheet writes a triad.
    maj: '',
    min: 'm',
    aug: '+',
    dim: '°',
  };
  const pretty = (name: string) => name.replace('b', '♭').replace('#', '♯');
  const bass = spec.bass ? `/${pretty(spec.bass)}` : '';
  return `${pretty(spec.root)}${suffix[spec.chordType] ?? spec.chordType}${bass}`;
};

export function proposal(
  ctx: ReharmContext,
  move: Pick<ReharmMove, 'id' | 'rung'>,
  at: number,
  chords: ReharmChordSpec[],
  rationale: string,
): Proposal {
  return {
    moveId: move.id,
    rung: move.rung,
    chordId: ctx.chordIds[at],
    measureId: ctx.measureIds[at],
    at,
    chords,
    label: chords.map(symbolOf).join(' '),
    rationale,
    changesOnsets: chords.length > 1,
  };
}

/** The replaced chord's written beats, split evenly `n` ways. */
export function split(ctx: ReharmContext, at: number, n: number): number[] {
  const beats = ctx.chordBeats[at];
  return Array.from({ length: n }, () => beats / n);
}

/** The chord's beats dealt into `n` slots the way a bar is read: even where
 *  it divides, else the FIRST slot takes the remainder (3 chords in a 4-beat
 *  bar read 2 + 1 + 1, a held opener then a walk). */
export function dealBeats(ctx: ReharmContext, at: number, n: number): number[] {
  const beats = ctx.chordBeats[at];
  const unit = Math.floor(beats / n) || beats / n;
  const out = Array.from({ length: n }, () => unit);
  out[0] += beats - unit * n;
  return out;
}

/** Felts's "strong root motion" (ch. 2): the two roots are NOT a third or a
 *  sixth apart — the approach into a target that makes it sound arrived at. */
export const strongRootMotion = (fromPc: number, toPc: number) =>
  ![3, 4, 8, 9].includes(mod12(toPc - fromPc));

/** The seventh chord on each scale degree of a key, degrees from the tonic.
 *  Minor carries both sevenths — the Aeolian ♭VII7 and the leading-tone °7 —
 *  because a minor-key tune writes both. */
export type DiatonicSeventh = { degree: number; type: ChordTypeId };
const MAJOR_SEVENTHS: DiatonicSeventh[] = [
  { degree: 0, type: 'maj7' },
  { degree: 2, type: 'min7' },
  { degree: 4, type: 'min7' },
  { degree: 5, type: 'maj7' },
  { degree: 7, type: 'dom7' },
  { degree: 9, type: 'min7' },
  { degree: 11, type: 'min7b5' },
];
const MINOR_SEVENTHS: DiatonicSeventh[] = [
  { degree: 0, type: 'min7' },
  { degree: 2, type: 'min7b5' },
  { degree: 3, type: 'maj7' },
  { degree: 5, type: 'min7' },
  { degree: 7, type: 'dom7' },
  { degree: 8, type: 'maj7' },
  { degree: 10, type: 'dom7' },
  { degree: 11, type: 'dim7' },
];
export const diatonicSevenths = (minor: boolean): readonly DiatonicSeventh[] =>
  minor ? MINOR_SEVENTHS : MAJOR_SEVENTHS;

/**
 * The chord restated for `beats` AS WRITTEN — its slash bass kept. Every move
 * that holds the chord (colour on it, the chord a ii–V leads into, the opener
 * of a line) goes through here, so applying a candidate never silently
 * rewrites the bass line: C∆/E coloured is C∆9/E, not C∆9.
 */
export function restate(
  chord: SequenceChord,
  beats: number,
  chordType: ChordTypeId = chord.chordType,
): ReharmChordSpec {
  return { root: chord.displayRoot, chordType, beats, ...(chord.bass ? { bass: chord.bass } : {}) };
}

/** A written bass that is not the chord's root (C∆/E, Dm7/G). The moves whose
 *  premise is that the root IS the bass — a pedal on it, a mode over it, a
 *  hybrid on it, a line walking under it — leave such a chord alone. */
export const hasSlashBass = (c: SequenceChord) =>
  !!c.bass && pitchClassOf(c.bass) !== pitchClassOf(c.root);

/** How the chord at `i` is HEARD (analysis.ts functionalReading): a hybrid
 *  reads from its bass, so Dm7/G is a V sus rather than a ii, while an
 *  inversion (C∆/E) keeps its root. Moves read the chords AROUND the one they
 *  replace through this, so a hybrid next door is the chord it sounds like. */
export const heard = (ctx: ReharmContext, i: number): FunctionalReading =>
  functionalReading(ctx.sequence, i);

/** The chord at `i` is heard from its bass — a hybrid. The moves that classify
 *  a chord by its written root and quality (the tritone sub needs a dominant,
 *  the diatonic families need a degree) would misread it, so they offer
 *  nothing on it; an inversion is still theirs. False past either end. */
export const heardFromBass = (ctx: ReharmContext, i: number): boolean =>
  !!ctx.sequence[i] && heard(ctx, i).rootPc !== pitchClassOf(ctx.sequence[i].root);

export const isDominant = (c: SequenceChord) => qualityFamily(c.chordType) === '7';
export const isSus = (c: SequenceChord) => c.chordType === 'dom7sus4';
